import { useEffect, useMemo, useState } from 'react';
import { DataFrame } from './data/DataFrame';
import {
  Alert,
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  Toggle,
} from '@cloudscape-design/components';
import {
  PageSkeleton,
  EmptyState,
  useAuth,
  useRoles,
  useNotify,
  createApiClient,
} from '@deloitte-us-consulting/gp-sdk';

/** AirTable data-access config served by GET/PUT /api/ai/airtable-config (admin only). */
interface AirtableConfig {
  enabled: boolean;
  baseId: string;
  tableName: string;
  view?: string | undefined;
  exposedFields: string[];
  suppressedFields: string[];
  maxRecords: number;
}

/** Real field/view names from GET /api/ai/airtable-schema — drives the picklists. */
interface AirtableSchema {
  fields: string[];
  views: string[];
}
interface SchemaTarget {
  baseId: string;
  tableName: string;
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

/**
 * Admin dashboard for platform data sources — currently AirTable, the source the
 * AI gateway reads from. Decides which base/table/view is exposed and which
 * fields are suppressed from the assistant. Gated to the Admin role (or gp-admin);
 * the API also enforces the same gate server-side. Structured so a second data
 * source can be added later as a sibling Container, without restructuring this page.
 */
export default function DataSourcesAdmin({ embedded = false }: { embedded?: boolean } = {}) {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Data:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [cfg, setCfg] = useState<AirtableConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Schema (real field/view names) is fetched only on explicit request — not
  // automatically, since baseId/tableName are free text and auto-fetching on
  // every keystroke would fire many invalid intermediate requests.
  const [schema, setSchema] = useState<AirtableSchema | null>(null);
  const [schemaFor, setSchemaFor] = useState<SchemaTarget | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<AirtableConfig>('/api/ai/airtable-config');
        if (!cancelled) setCfg(data);
      } catch (e) {
        if (!cancelled) {
          notify({ type: 'error', header: 'Could not load data sources', content: (e as Error).message });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Load once when admin status / api client settle. Intentionally NOT depending
    // on `notify` — it's a fresh function each render, which would re-run this
    // effect on every render and clobber the user's in-progress edits.
  }, [api, isAllowed]);

  if (!isAllowed) {
    return (
      <DataFrame embedded={embedded} title="Assistant data access">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </DataFrame>
    );
  }
  if (loading) {
    return (
      <DataFrame embedded={embedded} title="Assistant data access">
        <PageSkeleton label="Loading data sources…" />
      </DataFrame>
    );
  }
  if (!cfg) {
    return (
      <DataFrame embedded={embedded} title="Assistant data access">
        <EmptyState
          title="Couldn't load data sources"
          description="Check that the platform API is deployed and you hold the Admin role, then reload."
        />
      </DataFrame>
    );
  }

  const update = (patch: Partial<AirtableConfig>) => setCfg((c) => (c ? { ...c, ...patch } : c));

  const loadSchema = async () => {
    const target = { baseId: cfg.baseId, tableName: cfg.tableName };
    setSchemaLoading(true);
    setSchemaError(null);
    try {
      const data = await api.get<AirtableSchema>(
        `/api/ai/airtable-schema?baseId=${encodeURIComponent(target.baseId)}&tableName=${encodeURIComponent(target.tableName)}`,
      );
      setSchema(data);
      setSchemaFor(target);
    } catch (e) {
      setSchemaError((e as Error).message);
    } finally {
      setSchemaLoading(false);
    }
  };

  // True once baseId/tableName have been edited since the schema was last loaded
  // — the picklists below would otherwise silently show fields from the wrong table.
  const schemaStale = schemaFor !== null && (schemaFor.baseId !== cfg.baseId || schemaFor.tableName !== cfg.tableName);

  // Always merge the currently-saved value(s) into the picklist options, so a
  // pre-existing selection never silently disappears — whether schema hasn't
  // loaded yet, failed to load, or a field was renamed/removed in AirTable since
  // this config was last saved.
  const viewSelectOptions = () => {
    const names = new Set(schema?.views ?? []);
    if (cfg.view) names.add(cfg.view);
    return [{ value: '', label: 'All records (no view)' }, ...[...names].map((v) => ({ value: v, label: v }))];
  };
  const fieldMultiselectOptions = (selected: string[]) => {
    const names = new Set(schema?.fields ?? []);
    for (const f of selected) names.add(f);
    return [...names].map((v) => ({ value: v, label: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.put<AirtableConfig>('/api/ai/airtable-config', {
        enabled: cfg.enabled,
        baseId: cfg.baseId,
        tableName: cfg.tableName,
        view: cfg.view,
        exposedFields: cfg.exposedFields.filter((f) => f.trim().length > 0),
        suppressedFields: cfg.suppressedFields.filter((f) => f.trim().length > 0),
        maxRecords: cfg.maxRecords,
      });
      setCfg(saved);
      notify({ type: 'success', header: 'Data sources saved', content: 'AirTable configuration updated.' });
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DataFrame embedded={embedded}
      title="Assistant data access"
      description="What the platform assistant can read, and what it can't. Changes apply on the next request — no redeploy."
      actions={
        <Button variant="primary" loading={saving} onClick={save}>
          Save
        </Button>
      }
    >
      <Form>
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">AirTable</Header>}>
            <SpaceBetween size="l">
              <Toggle checked={cfg.enabled} onChange={({ detail }) => update({ enabled: detail.checked })}>
                Enabled
              </Toggle>

              <FormField label="Base ID">
                <Input value={cfg.baseId} onChange={({ detail }) => update({ baseId: detail.value })} />
              </FormField>
              <FormField label="Table" description="Table name or id — the master list the assistant reads.">
                <Input value={cfg.tableName} onChange={({ detail }) => update({ tableName: detail.value })} />
              </FormField>

              <Button onClick={loadSchema} loading={schemaLoading} disabled={!cfg.baseId.trim() || !cfg.tableName.trim()}>
                Load fields & views
              </Button>
              {schemaError ? (
                <Alert type="error">Could not load fields &amp; views from AirTable: {schemaError}</Alert>
              ) : null}
              {schemaStale ? (
                <Alert type="warning">
                  Base or table changed since fields &amp; views were loaded — the lists below may not match "
                  {cfg.baseId}" / "{cfg.tableName}". Click "Load fields &amp; views" again to refresh.
                </Alert>
              ) : null}

              <FormField label="View (optional)" description="Scope/filter records to a specific AirTable view.">
                <Select
                  selectedOption={{ value: cfg.view ?? '', label: cfg.view || 'All records (no view)' }}
                  options={viewSelectOptions()}
                  onChange={({ detail }) => update({ view: detail.selectedOption.value || undefined })}
                />
              </FormField>
              <FormField label="Max records" description="1–500 records returned per query. Fetched in pages of up to 100 records each.">
                <Input
                  type="number"
                  value={String(cfg.maxRecords)}
                  onChange={({ detail }) => update({ maxRecords: Number(detail.value) })}
                />
              </FormField>

              <FormField
                label="Exposed fields"
                description="Allow-list: if any are set, ONLY these fields are ever requested from AirTable. Leave empty to allow all fields (minus suppressed)."
              >
                <Multiselect
                  selectedOptions={cfg.exposedFields.map((v) => ({ value: v, label: v }))}
                  options={fieldMultiselectOptions(cfg.exposedFields)}
                  onChange={({ detail }) =>
                    update({
                      exposedFields: detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v),
                    })
                  }
                  placeholder="All fields (no restriction)"
                />
              </FormField>

              <FormField
                label="Suppressed fields"
                description="Deny-list: always stripped from every record, even if allow-listed above. This is how contracts and revenue projections stay hidden from the assistant."
              >
                <Multiselect
                  selectedOptions={cfg.suppressedFields.map((v) => ({ value: v, label: v }))}
                  options={fieldMultiselectOptions(cfg.suppressedFields)}
                  onChange={({ detail }) =>
                    update({
                      suppressedFields: detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v),
                    })
                  }
                  placeholder="No fields suppressed"
                />
              </FormField>

              <Box color="text-body-secondary" fontSize="body-s">
                The AirTable PAT itself lives in Secrets Manager — never entered here.
              </Box>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Form>
    </DataFrame>
  );
}
